<script setup lang="ts">
/**
 * La striscia «sei in videochiamata dal telefono · continua qui» (2026-10-28).
 *
 * È la metà web del passaggio fra PC e telefono che Cosimo ha scelto: chi è entrato in una
 * chiamata dall'app e si siede al PC trova qui la chiamata, e un tocco la porta sul Cruscotto —
 * il telefono esce da solo, perché il motore tiene una connessione per persona
 * (`DUPLICATE_IDENTITY`, che l'app racconta a parole).
 *
 * Il dato viene dalle presenze scritte dagli eventi del motore (`GET /videochiamate/io/altrove`),
 * non da una supposizione: se il telefono esce, al giro dopo la striscia se ne va da sola.
 *
 * ## Quanto chiede
 *
 * Una lettura ogni trenta secondi **solo a scheda visibile**, e una subito quando la scheda torna
 * in vista — che è esattamente il momento in cui serve: ci si siede al PC e si guarda lo schermo.
 * Una scheda dietro altre non chiede niente. Un errore non si mostra: la striscia è un aiuto, e
 * un Cruscotto senza videochiamate (o un server di prima) risponde con un rifiuto che qui vuol
 * dire soltanto «niente da dire».
 *
 * Sta nella colonna come `AvvisoVersioneNuova`, e per le stesse ragioni (vedi lì): nel flusso,
 * appiccicata sotto la barra, inerte col pannello mobile aperto.
 */

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Smartphone, X } from '@lucide/vue';
import Button from 'primevue/button';
import { videochiamate, type PresenzaAltrove } from '@/lib/videochiamate';

const OGNI = 30_000;

const router = useRouter();
const presenze = ref<PresenzaAltrove[]>([]);
/** Le chiamate per cui la striscia e' stata chiusa a mano, in questa scheda. */
const chiuse = ref(new Set<string>());

const dalTelefono = computed(
  () => presenze.value.find((p) => p.dispositivo === 'telefono' && !chiuse.value.has(p.codice)) ?? null,
);

let timer: ReturnType<typeof setTimeout> | null = null;
let inVolo: AbortController | null = null;
let spenta = false;

async function leggi(): Promise<void> {
  if (spenta || document.visibilityState !== 'visible') return;
  inVolo?.abort();
  const c = new AbortController();
  inVolo = c;
  try {
    presenze.value = await videochiamate.altrove(c.signal);
  } catch {
    if (!c.signal.aborted) presenze.value = [];
  }
}

function programma(): void {
  if (timer) clearTimeout(timer);
  // Smontata con una lettura in volo: il suo `finally` riarmava un giro senza fine.
  if (spenta) return;
  timer = setTimeout(() => {
    void leggi().finally(programma);
  }, OGNI);
}

function suVisibilita(): void {
  if (document.visibilityState === 'visible') {
    void leggi();
    programma();
  }
}

onMounted(() => {
  void leggi();
  programma();
  document.addEventListener('visibilitychange', suVisibilita);
});

onBeforeUnmount(() => {
  spenta = true;
  if (timer) clearTimeout(timer);
  inVolo?.abort();
  document.removeEventListener('visibilitychange', suVisibilita);
});

/* Il testo per chi ascolta arriva un battito dopo la striscia: vedi `AvvisoVersioneNuova`. */
const frase = ref('');
watch(
  dalTelefono,
  (p) => {
    if (!p) {
      frase.value = '';
      return;
    }
    void nextTick(() => {
      frase.value = p.titolo;
    });
  },
  { immediate: true },
);

function continuaQui(): void {
  const p = dalTelefono.value;
  if (!p) return;
  void router.push({ name: 'chiamata', params: { codice: p.codice } });
}

function chiudi(): void {
  const p = dalTelefono.value;
  if (!p) return;
  chiuse.value = new Set([...chiuse.value, p.codice]);
  document.getElementById('contenuto')?.focus({ preventScroll: true });
}
</script>

<template>
  <Transition name="avviso">
    <section v-if="dalTelefono" class="avviso" aria-label="Videochiamata in corso dal telefono">
      <div class="avviso__dentro">
        <span class="avviso__segno" aria-hidden="true">
          <Smartphone :size="18" :stroke-width="2" />
          <span class="avviso__polso" />
        </span>

        <div class="avviso__testo" role="status">
          <p class="avviso__titolo">Sei in videochiamata dal telefono</p>
          <p class="avviso__dettaglio">{{ frase }}</p>
        </div>

        <!-- Pieno: qui il tasto è la ragione della striscia, e passare non perde niente. -->
        <Button class="avviso__azione" label="Continua qui" data-testid="altrove-continua" @click="continuaQui" />

        <button type="button" class="avviso__chiudi" aria-label="Chiudi l'avviso" @click="chiudi">
          <X :size="18" :stroke-width="2" aria-hidden="true" />
        </button>
      </div>
    </section>
  </Transition>
</template>

<style scoped>
.avviso {
  position: sticky;
  top: var(--beste-layout-topbar);
  z-index: var(--beste-z-sticky);
  display: grid;
  grid-template-rows: 1fr;
  background: var(--beste-accent-surface);
  color: var(--beste-accent-text);
  border-bottom: 1px solid var(--beste-accent-border);
}

.avviso__dentro {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  grid-template-areas: 'segno testo azione chiudi';
  align-items: center;
  gap: var(--beste-space-8) var(--beste-space-12);
  min-height: 0;
  overflow: hidden;
  padding: var(--beste-space-8) var(--beste-space-16) var(--beste-space-8)
    var(--beste-layout-gutterDesktop);
}

/* Il telefono con un polso dietro: «adesso, in diretta», come il pallino delle chiamate in corso. */
.avviso__segno {
  grid-area: segno;
  position: relative;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: var(--beste-radius-full);
  background: var(--beste-accent-solid);
  color: var(--beste-text-onSolid);
}

.avviso__segno > svg {
  position: relative;
  z-index: 1;
}

.avviso__polso {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--beste-accent-solid);
  animation: polso 2.4s var(--beste-ease-standard) infinite;
}

@keyframes polso {
  0% {
    transform: scale(1);
    opacity: 0.45;
  }
  70%,
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .avviso__polso {
    animation: none;
    opacity: 0;
  }
}

.avviso__testo {
  grid-area: testo;
  min-width: 0;
}

.avviso__titolo,
.avviso__dettaglio {
  margin: 0;
  font-size: var(--beste-font-small-size);
  line-height: var(--beste-font-small-line);
}

.avviso__titolo {
  font-weight: var(--beste-font-bodyStrong-weight);
}

.avviso__dettaglio {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.avviso__azione {
  grid-area: azione;
  min-height: 44px;
  white-space: nowrap;
}

.avviso__chiudi {
  grid-area: chiudi;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--beste-radius-md);
  color: var(--beste-accent-text);
  cursor: pointer;
  transition: background-color var(--beste-duration-fast) var(--beste-ease-standard);
}

.avviso__chiudi:hover,
.avviso__chiudi:active {
  background: color-mix(in srgb, var(--beste-accent-border) 55%, transparent);
}

.avviso-enter-active {
  transition:
    grid-template-rows var(--beste-duration-normal) var(--beste-ease-enter),
    opacity var(--beste-duration-normal) var(--beste-ease-enter);
}

.avviso-leave-active {
  transition:
    grid-template-rows var(--beste-duration-fast) var(--beste-ease-exit),
    opacity var(--beste-duration-fast) var(--beste-ease-exit);
}

.avviso-enter-from,
.avviso-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

@media (max-width: 1023px) {
  .avviso__dentro {
    padding-inline-start: var(--beste-layout-gutterMobile);
  }
}

@media (max-width: 639px) {
  .avviso__dentro {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'segno testo chiudi'
      '. azione chiudi';
    align-items: start;
  }

  .avviso__azione {
    justify-self: start;
  }
}
</style>
